/* =============================================================================
   MENU DRAWER V2 — Custom element zarządzający multi-screen slide navigation
   ============================================================================= */

(function () {
  if (customElements.get('menu-drawer-v2')) return;

  class MenuDrawerV2 extends HTMLElement {
    constructor() {
      super();
      this.history = [];
      this.activeScreenId = 'root';
    }

    connectedCallback() {
      this.viewport = this.querySelector('.menu-drawer-v2__viewport');
      this.screens = new Map();
      this.querySelectorAll('.menu-drawer-v2__screen').forEach((el) => {
        const id = el.getAttribute('data-screen');
        if (id) this.screens.set(id, el);
      });

      this._bindTriggers();
      this._bindBackButtons();
      this._bindKeyboard();
      this._bindSwipe();
      this._bindLocaleSwitcher();
      this._applyState();
      this._observeOpen();
    }

    /**
     * Locale dropdown — gdy user kliknie na opcję, ustaw hidden input + submit
     * (bypassujemy theme'owy localization-form.js, który nie zawsze się inicjalizuje
     * w drawerze ze względu na visibility:hidden).
     *
     * Uwaga: preventDefault dopiero PO wykryciu <form> + data-value. Przełącznik
     * języka oparty o menu (naoko-language-switcher) używa tej samej klasy opcji,
     * ale to zwykłe linki <a href> bez formularza — muszą nawigować natywnie.
     */
    _bindLocaleSwitcher() {
      this.addEventListener('click', (e) => {
        const opt = e.target.closest('.menu-drawer-v2__locale-option');
        if (!opt || !this.contains(opt)) return;
        const form = opt.closest('form');
        if (!form) return;
        const value = opt.getAttribute('data-value');
        if (!value) return;
        e.preventDefault();
        const codeInput = form.querySelector('input[name="country_code"], input[name="locale_code"]');
        const returnInput = form.querySelector('input[name="return_to"]');
        if (codeInput) codeInput.value = value;
        if (returnInput) returnInput.value = window.location.pathname + window.location.search;
        form.submit();
      });
    }

    /**
     * Push current screen onto history and slide to new one.
     */
    goTo(screenId) {
      if (!this.screens.has(screenId)) return;
      if (screenId === this.activeScreenId) return;
      this.history.push(this.activeScreenId);
      this.activeScreenId = screenId;
      this._applyState();
      this._focusFirstInActive();
      this._announce(this._screenTitle(screenId));
    }

    /**
     * Pop history and slide back. If history empty, no-op.
     */
    back() {
      if (this.history.length === 0) return false;
      const prevId = this.history.pop();
      const fromScreenId = this.activeScreenId;
      this.activeScreenId = prevId;
      this._applyState();
      this._restoreFocusInPrev(fromScreenId);
      this._announce(this._screenTitle(prevId));
      return true;
    }

    /**
     * Reset to root (e.g. when drawer closes).
     */
    reset() {
      this.history = [];
      this.activeScreenId = 'root';
      this._applyState();
    }

    /* ─── Internals ─────────────────────────────────────────────────────── */

    _bindTriggers() {
      this.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-go-to]');
        if (trigger && this.contains(trigger)) {
          e.preventDefault();
          const target = trigger.getAttribute('data-go-to');
          this._lastTriggerByScreen = this._lastTriggerByScreen || new Map();
          this._lastTriggerByScreen.set(target, trigger);
          this.goTo(target);
        }
      });
    }

    _bindBackButtons() {
      this.addEventListener('click', (e) => {
        const backBtn = e.target.closest('[data-mdv2-back]');
        if (backBtn && this.contains(backBtn)) {
          e.preventDefault();
          this.back();
        }
      });
    }

    _bindKeyboard() {
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (this.history.length > 0) {
            e.stopPropagation();
            e.preventDefault();
            this.back();
          }
          // else: let side-drawer close on ESC
        }
      });
    }

    /**
     * Touch swipe-back gesture (iOS-style):
     *  - Swipe RIGHTWARD (finger from left → right) cofa do poprzedniego ekranu.
     *  - Aktywne tylko gdy mamy historię (nie na root).
     *  - Ignorujemy gesty na elementach scrollowanych poziomo i interaktywnych
     *    do których user pewnie celował (np. links).
     */
    _bindSwipe() {
      if (!('ontouchstart' in window) && !(navigator.maxTouchPoints > 0)) return;

      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let tracking = false;
      const THRESHOLD_X = 70;       // min px do uznania jako swipe
      const MAX_OFF_AXIS = 50;      // max vertical drift żeby nie konfliktować z scrollem
      const MAX_DURATION = 600;     // szybki swipe (ms)
      const LONG_DISTANCE = Math.max(120, window.innerWidth * 0.25); // długi powolny swipe też ok

      const onStart = (e) => {
        if (this.history.length === 0) return; // brak ekranu do cofnięcia
        const t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        startTime = Date.now();
        tracking = true;
      };

      const onMove = (e) => {
        if (!tracking) return;
        const t = e.touches[0];
        const dx = t.clientX - startX;
        const dy = Math.abs(t.clientY - startY);
        // Jeśli gest jest głównie pionowy — porzuć (to scroll)
        if (dy > MAX_OFF_AXIS && dy > Math.abs(dx)) {
          tracking = false;
        }
      };

      const onEnd = (e) => {
        if (!tracking) return;
        tracking = false;
        const t = (e.changedTouches && e.changedTouches[0]) || null;
        if (!t) return;
        const dx = t.clientX - startX;
        const dy = Math.abs(t.clientY - startY);
        const duration = Date.now() - startTime;
        const isHorizontal = Math.abs(dx) > dy + 10;
        const isRightward = dx > 0;
        const isLongEnough = dx > LONG_DISTANCE;
        const isQuickEnough = duration < MAX_DURATION && dx > THRESHOLD_X;
        if (isHorizontal && isRightward && (isQuickEnough || isLongEnough)) {
          this.back();
        }
      };

      this.addEventListener('touchstart', onStart, { passive: true });
      this.addEventListener('touchmove', onMove, { passive: true });
      this.addEventListener('touchend', onEnd, { passive: true });
      this.addEventListener('touchcancel', () => { tracking = false; }, { passive: true });
    }

    _applyState() {
      this.screens.forEach((el, id) => {
        if (id === this.activeScreenId) {
          el.setAttribute('data-state', 'active');
          el.removeAttribute('aria-hidden');
          el.removeAttribute('inert');
        } else if (this.history.includes(id)) {
          el.setAttribute('data-state', 'behind');
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('inert', '');
        } else {
          el.setAttribute('data-state', 'ahead');
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('inert', '');
        }
      });
    }

    _focusFirstInActive() {
      const screen = this.screens.get(this.activeScreenId);
      if (!screen) return;
      // Focus first interactive within content area (skip header)
      const target = screen.querySelector(
        '[data-mdv2-back], .menu-drawer-v2__content a, .menu-drawer-v2__content button, .menu-drawer-v2__content [tabindex]:not([tabindex="-1"])'
      );
      if (target) {
        // Defer slightly so transition starts
        requestAnimationFrame(() => target.focus({ preventScroll: true }));
      }
    }

    _restoreFocusInPrev(fromScreenId) {
      // Try to focus the trigger that originally opened the screen we just left
      if (this._lastTriggerByScreen && this._lastTriggerByScreen.has(fromScreenId)) {
        const trigger = this._lastTriggerByScreen.get(fromScreenId);
        if (trigger && document.contains(trigger)) {
          requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
        }
      }
    }

    _screenTitle(screenId) {
      const screen = this.screens.get(screenId);
      if (!screen) return '';
      return (
        screen.getAttribute('data-screen-title') ||
        screen.querySelector('.menu-drawer-v2__back')?.textContent.trim() ||
        ''
      );
    }

    _announce(text) {
      if (!text) return;
      let live = this.querySelector('.menu-drawer-v2__live');
      if (!live) {
        live = document.createElement('div');
        live.className = 'menu-drawer-v2__live visually-hidden';
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('aria-atomic', 'true');
        this.appendChild(live);
      }
      live.textContent = text;
    }

    _observeOpen() {
      // Reset do roota dopiero gdy side-drawer w pełni się zamknie.
      // SideDrawer steruje stanem KLASAMI (.open/.active), nie atrybutem `open`,
      // więc obserwujemy `class`. close() najpierw zdejmuje .active (slajd-out),
      // a na końcu .open — brak obu = zamknięte. Dzięki temu reset nie przeskakuje
      // ekranu w trakcie animacji zamykania (wcześniej obserwator czekał na atrybut
      // `open`, który nigdy się nie pojawiał → reset w ogóle nie działał).
      const sideDrawer = this.closest('side-drawer');
      if (!sideDrawer) return;
      const observer = new MutationObserver(() => {
        if (!sideDrawer.classList.contains('open') && !sideDrawer.classList.contains('active')) {
          this.reset();
        }
      });
      observer.observe(sideDrawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  customElements.define('menu-drawer-v2', MenuDrawerV2);
})();
